import React, { useState, useEffect } from 'react';
import { timeParse, timeFormat } from 'd3';

import Graph from '../../base/megatable/graph';
import GranularitiesDropdown from '../../base/dropdowns/granularities-dropdown';
import { Granularities } from '../../base/megatable/models';
import { Loader, NoResult } from '../../misc/loader-utils';

import { session } from '../../../services/session';
import { api } from '../../../services/api';
import { getTicks, processStats } from '../../base/megatable/graph-utils';
import { determineGranularities } from './utils';

import '../../base/megatable/megatable-graph.scss';

const { defaultGranularities } = new Granularities();
const chartOptions = [
  {
    label: 'Revenue',
    statKey: 'revenue',
    dataType: 'currency',
    d3Format: '$',
    color: 'orange',
  },
  {
    label: 'Spend',
    statKey: 'spend',
    dataType: 'currency',
    d3Format: '$',
    color: 'purple',
  },
  {
    label: 'Clicks',
    statKey: 'clicks',
    dataType: 'number',
    d3Format: '',
    color: 'blue',
  },
  {
    label: 'RPC',
    statKey: 'rpc',
    dataType: 'currency',
    d3Format: '$',
    color: 'green',
  },
];

function StrategyPerformanceGraph({ strategyId, dateRange }) {
  /* State */
  const [stats, setStats] = useState([]);
  const [ticks, setTicks] = useState([]);
  const [option, setOption] = useState(chartOptions[0]);
  const [granularities, setGranularities] = useState(
    determineGranularities(dateRange, defaultGranularities),
  );
  const [loading, setLoading] = useState(true);
  const [showNoResult, setShowNoResult] = useState(false);
  /* Constants */
  const endpoint = `/api/v0/merchants/${session.orgId}/strategies/${strategyId}/stats/`;
  const selectedGranularity = granularities.filter(item => item.selected)[0].name;
  const parseDate = timeParse('%Y-%m-%d');
  const formattedDateRange = {
    date_from: dateRange.startDate.format('YYYY-MM-DD'),
    date_to: dateRange.endDate.format('YYYY-MM-DD'),
  };

  useEffect(() => {
    setLoading(true);
    const gParam = ['daily', 'weekly'].includes(selectedGranularity) ? 'daily' : 'monthly';

    api
      .get(endpoint, {
        date_from: formattedDateRange.date_from,
        date_to: formattedDateRange.date_to,
        granularity: gParam,
      })
      .then(resp => {
        const respData = resp.data.data[0].stats;
        if (!respData.length) {
          setShowNoResult(true);
          setLoading(false);
          return;
        }
        const ticks = getTicks(
          selectedGranularity,
          formattedDateRange.date_from,
          formattedDateRange.date_to,
        );
        const stats = processStats(
          respData,
          ticks,
          selectedGranularity,
          formattedDateRange,
          option,
        );
        setStats(stats);
        setTicks(ticks);
        setLoading(false);
        setShowNoResult(false);
      })
      .catch(() => {
        setLoading(false);
        setShowNoResult(true);
      });
  }, [dateRange, selectedGranularity]);

  useEffect(() => {
    setGranularities(determineGranularities(dateRange, granularities));
  }, [dateRange]);

  /* Render */
  const optionMenu = (
    <div className="option-menu">
      {chartOptions.map((chartOption, i) => {
        const selected = option.statKey === chartOption.statKey;
        return (
          <div
            key={i}
            className={`chart-btn ${chartOption.color} ${selected ? 'selected' : ''}`}
            onClick={() => {
              setStats([{ data: stats[0].data, color: chartOption.color }]);
              setOption(chartOption);
            }}
          >
            <span className="square" />
            {chartOption.label}
          </div>
        );
      })}
    </div>
  );

  const menu =
    !loading && !showNoResult ? (
      <div className="menu">
        {optionMenu}
        <GranularitiesDropdown
          gaEvent={{
            category: 'Strategy Performance Graph',
            label: `Granularity`,
          }}
          items={granularities}
          updateGranularities={g => setGranularities(g)}
          showGraph={true}
        />
      </div>
    ) : null;

  const graph =
    !loading && !showNoResult ? (
      <Graph
        stats={stats}
        xTicks={ticks}
        chartOptions={option}
        dates={formattedDateRange}
        granularity={selectedGranularity}
        merchantPerformance={true}
      />
    ) : null;

  return (
    <div className="strategy-performance-graph megatable-graph">
      {menu}
      {graph}
      <Loader loading={loading} />
      <NoResult noResult={showNoResult} type="strategy-performance" />
    </div>
  );
}

export default StrategyPerformanceGraph;
